import { PostPersistantData } from "../Components/Posts/Post/Post";
import {
  BooleanConfirmationDto,
  RoleDto,
  StringConfirmationDto,
} from "./dto/Dto";
import { PostDto_GET } from "./dto/PostDto_GET";
import { PostDto_POST_PUT } from "./dto/PostDto_POST_PUT";
import { ResultDto_GET } from "./dto/ResultDto_GET";
import { ResultExtendedDto_GET } from "./dto/ResultExtendedDto_GET";
import {
  requestDELETE,
  requestGET,
  requestPOST,
  requestPUT,
  successfulResponse,
} from "./HttpRequestHelpers";

/////////////////////////////////////////////////////////////////
/////////      ResultApi          ///////////////////////////////
/////////////////////////////////////////////////////////////////
export const resultApi = {
  getResults: async (competitionTag: string) => {
    const staticFileResults = await requestGET<ResultDto_GET[]>(
      `/media/results/${competitionTag}`
    );
    if (staticFileResults.body != null && staticFileResults.body.length > 0) {
      return staticFileResults.body;
    }

    const dbResults = await requestGET<ResultDto_GET[]>(
      `/api/competition/${competitionTag}/results`
    );
    if (dbResults.body != null && dbResults.body.length > 0) {
      return dbResults.body;
    }

    return [] as ResultDto_GET[];
  },
  getExtendedResults: async (disciplineIds: number[], competitionTag: string) => {
    const query = new URLSearchParams();

    disciplineIds.forEach((id) => {
      query.append("discipline_id", String(id));
    });

    const response = await requestGET<ResultExtendedDto_GET[]>(
      `/api/competition/${competitionTag}/extended-results?${query.toString()}`
    );

    return response.body ?? [];
  }
};

/////////////////////////////////////////////////////////////////
/////////      AdminPostApi       ///////////////////////////////
/////////////////////////////////////////////////////////////////
export const adminPostApi = {
  logout: () => requestGET<BooleanConfirmationDto>("/api/session/logout"),

  postPost: async (category: string, post: PostPersistantData) => {
    const body: PostDto_POST_PUT = createPostDto_POST_PUT(post);

    const response = await requestPOST<PostDto_GET>(
      `/api/posts/${category}`,
      body
    );

    if (response.body) {
      return createPost(response.body);
    }
    throw new Error("Saving post failed");
  },

  putPost: async (category: string, id: string, post: PostPersistantData) => {
    console.log(`PUT: ${category}, ${id}, ${post}`);
    if (id === "") {
      throw Error("Can not update post with ID=''");
    }
    const body: PostDto_POST_PUT = createPostDto_POST_PUT(post);

    const response = await requestPUT<PostDto_GET>(
      `/api/posts/${category}/${id}`,
      body
    );

    if (response.body) {
      return createPost(response.body);
    }
    throw new Error("Saving post failed");
  },

  deletePost: async (category: string, id: string) => {
    if (id === "") {
      throw Error("Can not delete post with ID=''");
    }
    const response = await requestDELETE<PostDto_GET>(
      `/api/posts/${category}/${id}`
    );

    if (response.body) {
      return createPost(response.body);
    }
    throw new Error("Deleting post failed");
  },

  getAllPosts: async (category: string) => {
    const response = await requestGET<PostDto_GET[]>(
      `/api/posts/${category}/all`
    );
    if (response.body) {
      return {
        payload: response.body.map((post) => createPost(post)),
        success: successfulResponse(response.status),
      };
    }
    return {
      payload: [] as PostPersistantData[],
      success: successfulResponse(response.status),
    };
  },
};

export const postApi = {
  getPosts: async (category: string) => {
    const response = await requestGET<PostDto_GET[]>(`/api/posts/${category}`);
    if (response.body) {
      return {
        payload: response.body.map((post) => createPost(post)),
        success: successfulResponse(response.status),
      };
    }
    return {
      payload: [] as PostPersistantData[],
      success: successfulResponse(response.status),
    };
  },
};

/////////////////////////////////////////////////////////////////
/////////      sessionApi         ///////////////////////////////
/////////////////////////////////////////////////////////////////
export const sessionApi = {
  ping: () => requestGET<StringConfirmationDto>("/api/session/ping"),
  getRole: () => requestGET<RoleDto>("/api/session/role"),
  logOut: () => requestGET<BooleanConfirmationDto>("/api/session/logout"),
};

export const testApi = {
  login: () => requestGET<BooleanConfirmationDto>("/api/test/login"),
};

/////////////////////////////////////////////////////////////////
/////////////////////////////////////////////////////////////////
/////////////////////////////////////////////////////////////////
/////////////////////////////////////////////////////////////////
/////////////////////////////////////////////////////////////////
/////////////////////////////////////////////////////////////////
/////////      Mapping functions  ///////////////////////////////
/////////////////////////////////////////////////////////////////
function createPostDto_POST_PUT(post: PostPersistantData): PostDto_POST_PUT {
  return {
    hideForcefully: post.hidden,
    showStartEpoch: post.timing?.showStartEpoch?.getTime(),
    showEndEpoch: post.timing?.showEndEpoch?.getTime(),
    pinTimeEpoch: post.pinTime,
    fractions: JSON.stringify(post.fractions),
    foldname: post.foldname,
  };
}

export function createPost(postDto: PostDto_GET): PostPersistantData {
  const fractions = JSON.parse(postDto.fractions);
  return {
    fractions: fractions,
    id: postDto.id,
    hidden: postDto.hideForcefully,
    timing: {
      showStartEpoch: postDto.showStartEpoch
        ? new Date(postDto.showStartEpoch)
        : undefined,
      showEndEpoch: postDto.showEndEpoch
        ? new Date(postDto.showEndEpoch)
        : undefined,
    },
    lastEditedTime: postDto.modificationTime,
    createTime: postDto.creationTime,
    pinTime: postDto.pinTimeEpoch,
    foldname: postDto.foldname ?? undefined,
  };
}
